import React, {useReducer} from 'react';
import {Context} from './Context';
import {initialState, reducer} from './Reducer';
import {IContext, ACTION_VERBS} from './Types';
import useUserContextState from '../user/Context';

import * as apiService from '../../services';

import {ProofType, Role} from '../../constants/enumerations';
import {
  IBalanceSheetResponse,
  IC2PPaymentBaseResponse,
  IClientListResponse,
  IDebtorListResponse,
  IFeeInvoiceCandidateListResponse,
  IHistoryResponse,
  IReceivableListResponse,
} from '../../interfaces/services';
import {IBalanceListResponse} from '../../interfaces/services/balances.interface';
import {INotificationListResponse} from '../../interfaces/services/notification-list-response.interface';
import {ResultState} from '../../constants/enumerations/state.enum';
import INotification from '../../interfaces/dtos/notification.interface';
import {IPaymentForm, ITaxForm} from '../../interfaces/forms';
import {IStandardServiceResponse} from '../../interfaces/services/standard-service.interface';
import {
  IBalanceSheetItem,
  IC2PPaymentFee,
  IClient,
  IDebtorItem,
  IDocumentDto,
  IPaymentProof,
  IReceivable,
  ISemaphoreValue,
  ITaxProof,
} from '../../interfaces/dtos';
import {ISemaphoreResponse} from '../../interfaces/services/semaphore-response.interface';
import {handleError} from '../../utils/exceptions/handle-error';
import {IDocumentListResponse} from '../../interfaces/services/document-list.interface';
import IInvoiceFeeSummary from '../../interfaces/dtos/invoice-fee-summary.interface';
import IInvoiceFee from '../../interfaces/dtos/invoice-fee.interface';
import {IBasePaymentC2P} from '../../interfaces/dtos/base-payment-c2p.interface';
import {IC2PPaymentForm} from '../../interfaces/forms/c2p-payment-form.interface';
import {IFeePaymentProofForm} from '../../interfaces/forms/fee-payment-proof-form.interface';
import {IFeePaymentProof} from '../../interfaces/dtos/fee-payment-proof.interface';
import {IPostC2pPaymentResponse} from '../../interfaces/services/responses';
import {IC2PPaymentFormConfirmation} from '../../interfaces/forms/c2p-payment-form-confirmation.interface';
import {IC2PPaymentFeeConfirmation} from '../../interfaces/dtos/c2p-payment-fee-confirmation.interface';
import {ICreditCardPaymentForm} from '../../interfaces/forms/credit-card-payment-form.interface';
import {ICreditCardPaymentFee} from '../../interfaces/dtos/credit-card-payment-fee.interface';

/**
 *
 * @param props
 * @returns
 */
export const SubClientProvider = (props: any) => {
  const {
    STATE_PROCESS_ERROR,
    BALANCE_LIST,
    NOTIFICATION_LIST,
    SEND_PROOF,
    SEND_EMAIL_BALANCE,
    BALANCE_SHEET,
    HISTORY,
    SEMAPHORE_ITEMS,
    DEBTOR_LIST,
    RECEIVABLE_LIST,
    DOCUMENT_LIST,
    GET_SALESMAN_CLIENTS,
    SET_SALESMAN_CLIENT,
    FETCH_INVOICE_FEE_SUMARY_LIST,
    SET_INVOICE_FEE_SUMMARY,
    SET_INVOICE_FEE_LIST_TO_PAY,
    FETCH_BASE_PAYMENT_C2P,
    SEND_PAYMENT_C2P,
    SEND_PROOF_FEE_PAYMENT,
    SEND_PROOF_FEE_PAYMENT_CONFIRMATION,
    SEND_CREDIT_CARD_PAYMENT,
  } = ACTION_VERBS;

  /**
   *
   */
  const [state, dispatch] = useReducer(reducer, initialState);

  const {getToken, profile, setRemoteMessageError} = useUserContextState();

  const getHistory = (response: IHistoryResponse) => {
    if (response.payload) {
      return response.payload;
    }
    return [];
  };

  const getBalanceList = (response: IBalanceListResponse) => {
    if (response.payload) {
      return response.payload;
    }
    return [];
  };

  const getInvoiceFeeSummaryList = (
    response: IFeeInvoiceCandidateListResponse,
  ): IInvoiceFeeSummary[] => {
    if (response.payload) {
      return response.payload;
    }
    return [];
  };

  const getBasePaymentC2P = (
    response: IC2PPaymentBaseResponse | null,
  ): IBasePaymentC2P | null => {
    if (response && response.payload) {
      return response.payload;
    }
    return null;
  };

  const getBalanceSheet = (
    response: IBalanceSheetResponse,
  ): IBalanceSheetItem | null => {
    if (response.payload) {
      return response.payload[0];
    }
    return null;
  };

  const getNotificationList = (
    response: INotificationListResponse,
  ): INotification[] => {
    if (response.payload) {
      return response.payload;
    }
    return [];
  };

  const getDebtorList = (response: IDebtorListResponse): IDebtorItem[] => {
    if (response.payload) {
      return response.payload;
    }
    return [];
  };

  const getReceivableList = (
    response: IReceivableListResponse,
  ): IReceivable[] => {
    if (response.payload) {
      return response.payload;
    }
    return [];
  };

  const formatNumber = (value: number): string => {
    return value.toString().padStart(2, '0');
  };

  const formatDate = (date: Date | undefined) => {
    if (date) {
      return `${date.getFullYear()}${formatNumber(
        date.getMonth() + 1,
      )}${formatNumber(date.getUTCDate())}`;
    }
    return '';
  };

  const getSemaphoreItems = (
    response: ISemaphoreResponse,
  ): ISemaphoreValue[] => {
    if (response.payload) {
      return response.payload;
    }
    return [];
  };

  const getDocumentList = (response: IDocumentListResponse): IDocumentDto[] => {
    if (response.payload) {
      return response.payload;
    }
    return [];
  };

  const getClientList = (response: IClientListResponse): IClient[] => {
    if (response.payload) {
      return response.payload;
    }
    return [];
  };

  const preparePaymentProof = (form: IPaymentForm): IPaymentProof => {
    return {
      amount: form.amount as number,
      bank: form.bank as string,
      date: formatDate(form.date),
      file: {
        name: form.file ? form.file.name : '',
        data: form.file && form.file.content ? form.file.content : '',
      },
      voucher: form.voucher as string,
      comments: form.comments ?? '',
      source: form.source ?? '',
    };
  };

  const prepareCreditCardPayment = (
    form: ICreditCardPaymentForm,
  ): ICreditCardPaymentFee => {
    const {number, name, expire, ccv, amount, invoiceId, invoiceFees} = form;

    return {
      amount: amount as number,
      invoice: {
        id: invoiceId ?? '',
        fees: invoiceFees ?? [],
      },

      creditCard: {
        number,
        name,
        expire,
        ccv,
      },
    };
  };

  const prepareC2PPayment = (form: IC2PPaymentForm): IC2PPaymentFee => {
    const {
      amount,
      bank,
      invoiceId,
      invoiceFees,
      documentNumber,
      documentType,
      phonePrefix,
      phoneNumber,
    } = form;

    return {
      amount: amount as number,
      bank: bank as string,
      invoice: {
        id: invoiceId ?? '',
        fees: invoiceFees ?? [],
      },
      document: {
        number: documentNumber ?? '',
        type: documentType ?? '',
      },
      phone: {
        prefix: phonePrefix ?? '',
        number: phoneNumber ?? '',
      },
    };
  };

  const prepareC2PPaymentConfirmation = (
    form: IC2PPaymentFormConfirmation,
  ): IC2PPaymentFeeConfirmation => {
    const {transactionId, c2pKey} = form;

    return {
      id: transactionId ?? '',
      key: c2pKey ?? '',
    };
  };

  const prepareFeePaymentProof = (
    form: IFeePaymentProofForm,
  ): IFeePaymentProof => {
    const {invoice, fees, source} = form;

    return {
      amount: form.amount as number,
      bank: form.bank as string,
      date: formatDate(form.date),
      file: {
        name: form.file ? form.file.name : '',
        data: form.file && form.file.content ? form.file.content : '',
      },
      voucher: form.voucher as string,
      comments: form.comments ?? '',
      invoice,
      fees,
      ...(source ? {source} : {}),
    };
  };

  const prepareTaxProof = (form: IPaymentForm): ITaxProof => {
    return {
      amount: form.amount as number,
      date: formatDate(form.date),
      file: {
        name: form.file ? form.file.name : '',
        data: form.file && form.file.content ? form.file.content : '',
      },
      voucher: form.voucher as string,
      comments: form.comments ?? '',
    };
  };

  /**
   *
   * @returns
   */
  const fetchBalanceList = async (): Promise<boolean> => {
    try {
      const response: IBalanceListResponse = await apiService.getBalanceList(
        getToken(),
      );

      if (response.success) {
        const balances = getBalanceList(response);

        dispatch({
          type: BALANCE_LIST,
          payload: balances,
        });
        return true;
      }
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, BALANCE_LIST);
    }
    return false;
  };

  /**
   *
   * @returns
   */
  const fetchHistoryList = async (): Promise<boolean> => {
    try {
      const response: IHistoryResponse = await apiService.getHistory(
        getToken(),
      );

      if (response.success) {
        const history = getHistory(response);

        dispatch({
          type: HISTORY,
          payload: history,
        });
        return true;
      }
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, HISTORY);
    }
    return false;
  };

  /**
   *
   * @returns
   */
  const fetchBalanceSheet = async (): Promise<boolean> => {
    try {
      const response: IBalanceSheetResponse = await apiService.getBalanceSheet(
        getToken(),
      );

      if (response.success) {
        const balance = getBalanceSheet(response);

        dispatch({
          type: BALANCE_SHEET,
          payload: balance,
        });
        return true;
      }
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, BALANCE_LIST);
    }
    return false;
  };

  const fetchNotificationList = async (read?: boolean): Promise<boolean> => {
    try {
      const response: INotificationListResponse =
        await apiService.getNotificationList(getToken(), read);

      if (response.success) {
        const notifications = getNotificationList(response);
        dispatch({
          type: NOTIFICATION_LIST,
          payload: notifications,
        });
        return true;
      }
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, NOTIFICATION_LIST);
    }
    return false;
  };

  const fetchSemaphoreItems = async (): Promise<boolean> => {
    try {
      const response: ISemaphoreResponse = await apiService.getSemphoreItems(
        getToken(),
      );

      if (response.success) {
        const semaphoreItems = getSemaphoreItems(response);
        dispatch({
          type: SEMAPHORE_ITEMS,
          payload: semaphoreItems,
        });
        return true;
      }
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, SEMAPHORE_ITEMS);
    }
    return false;
  };

  const fetchSalesmanClients = async (): Promise<boolean> => {
    try {
      const response: IClientListResponse = await apiService.getSalesmanClients(
        getToken(),
      );

      if (response.success) {
        const clients = getClientList(response);
        dispatch({
          type: GET_SALESMAN_CLIENTS,
          payload: clients,
        });
        return true;
      }
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, SEMAPHORE_ITEMS);
    }
    return false;
  };

  const fetchDebtorList = async (key: string): Promise<boolean> => {
    try {
      const response: IDebtorListResponse = await apiService.getDebtorsList(
        key,
        getToken(),
      );

      if (response.success) {
        const documents = getDebtorList(response);
        dispatch({
          type: DEBTOR_LIST,
          payload: documents,
        });
        return true;
      }
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, DEBTOR_LIST);
    }
    return false;
  };

  const fetchReceivableListByDebtor = async (
    key: string,
    rif: string,
  ): Promise<boolean> => {
    try {
      const response: IReceivableListResponse =
        await apiService.getReceivablesByDebtor(getToken(), key, rif);

      console.info('fetchReceivableListByDebtor response', response);

      if (response.success) {
        const payload = getReceivableList(response);
        dispatch({
          type: RECEIVABLE_LIST,
          payload,
        });
        return true;
      }
    } catch (error) {
      console.info('fetchReceivableListByDebtor error', error);
      handleError(error, STATE_PROCESS_ERROR, RECEIVABLE_LIST);
    }
    return false;
  };

  const sendProof = async (
    form: IPaymentForm | ITaxForm,
    type: ProofType,
  ): Promise<boolean> => {
    const token = getToken();
    const proof =
      type === ProofType.payment
        ? preparePaymentProof(form as IPaymentForm)
        : prepareTaxProof(form as IPaymentForm);
    try {
      let response: IStandardServiceResponse =
        await apiService.postPaymentProof(
          {
            proof,
            type,
          },
          token,
        );

      if (response.success) {
        return true;
      } else if (
        response.error &&
        response.error.code === '422' &&
        response.error.message
      ) {
        setRemoteMessageError(response.error.message);
      }
    } catch (e) {
      handleError(e, STATE_PROCESS_ERROR, SEND_PROOF);
    }
    return false;
  };

  const sendEmailBalance = async (
    id: string,
    typeCode: string,
  ): Promise<ResultState> => {
    try {
      const response = await apiService.postSendEmailBalance(
        id,
        typeCode,
        getToken(),
        isSalesMan(),
      );
      const {success} = response;
      if (success) {
        dispatch({
          type: SEND_EMAIL_BALANCE,
        });
        return ResultState.success;
      }
      return ResultState.rejected;
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, SEND_EMAIL_BALANCE);
    }
    return ResultState.error;
  };

  const getRole = () => {
    return profile?.role;
  };

  const isSalesMan = (): boolean => {
    if (!isAnonymous() && getRole() === Role.salesman) {
      return true;
    }
    return false;
  };

  const isAnonymous = (): boolean => {
    if (
      state.session &&
      state.session.begin &&
      state.session.token &&
      state.session.lifetime &&
      state.session.signed === true
    ) {
      return false;
    }
    return true;
  };

  const setSalesmanClient = (client: IClient | null) => {
    dispatch({
      type: SET_SALESMAN_CLIENT,
      payload: client,
    });
    return true;
  };

  const fetchDocumentList = async (): Promise<boolean> => {
    try {
      const response: IDocumentListResponse = await apiService.getDocumentList(
        getToken(),
      );

      if (response.success) {
        const documents = getDocumentList(response);
        dispatch({
          type: DOCUMENT_LIST,
          payload: documents,
        });
        return true;
      }
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, DOCUMENT_LIST);
    }
    return false;
  };

  /**
   *
   * @returns
   */
  const fetchInvoiceFeeSummaryList = async (): Promise<boolean> => {
    try {
      const response: IFeeInvoiceCandidateListResponse =
        await apiService.getInvoiceFeeCandidates(getToken());

      if (response.success) {
        const list = getInvoiceFeeSummaryList(response);

        dispatch({
          type: FETCH_INVOICE_FEE_SUMARY_LIST,
          payload: list,
        });
        return true;
      }
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, FETCH_INVOICE_FEE_SUMARY_LIST);
    }
    return false;
  };

  const setInvoiceFeeSummary = (invoiceFee: IInvoiceFeeSummary) => {
    dispatch({
      type: SET_INVOICE_FEE_SUMMARY,
      payload: invoiceFee,
    });
  };

  const setInvoiceFeeListToPay = (list: IInvoiceFee[] | null) => {
    dispatch({
      type: SET_INVOICE_FEE_LIST_TO_PAY,
      payload: list,
    });
  };

  /**
   *
   * @returns
   */
  const fetchBasePaymentC2P = async (): Promise<boolean> => {
    try {
      const response: IC2PPaymentBaseResponse =
        await apiService.getC2PPaymentBase(getToken());

      if (response.success) {
        const c2pBase = getBasePaymentC2P(response);

        dispatch({
          type: FETCH_BASE_PAYMENT_C2P,
          payload: c2pBase,
        });
        return true;
      }
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, FETCH_BASE_PAYMENT_C2P);
    }
    return false;
  };

  const sendPaymentCreditCard = async (
    form: ICreditCardPaymentForm,
  ): Promise<boolean> => {
    const payment = prepareCreditCardPayment(form);
    try {
      let response: IPostC2pPaymentResponse =
        await apiService.postCreditCardPaymentFee(payment, getToken());

      if (response.success) {
        dispatch({
          type: SEND_CREDIT_CARD_PAYMENT,
          payload: null,
        });
        return true;
      }
    } catch (e) {
      handleError(e, STATE_PROCESS_ERROR, SEND_CREDIT_CARD_PAYMENT);
    }
    return false;
  };

  const sendPaymentC2P = async (form: IC2PPaymentForm): Promise<boolean> => {
    const payment = prepareC2PPayment(form);
    try {
      let response: IPostC2pPaymentResponse =
        await apiService.postC2PPaymentFee(payment, getToken());

      if (response.success) {
        dispatch({
          type: SEND_PAYMENT_C2P,
          payload: response.payload,
        });
        return true;
      }
    } catch (e) {
      handleError(e, STATE_PROCESS_ERROR, SEND_PAYMENT_C2P);
    }
    return false;
  };

  const sendPaymentC2PConfirmation = async (
    form: IC2PPaymentFormConfirmation,
  ): Promise<boolean> => {
    const payment = prepareC2PPaymentConfirmation(form);
    try {
      let response: IStandardServiceResponse =
        await apiService.postC2PPaymentFeeConfirmation(payment, getToken());

      if (response.success) {
        dispatch({
          type: SEND_PROOF_FEE_PAYMENT_CONFIRMATION,
          payload: response.payload,
        });
        return true;
      }
    } catch (e) {
      handleError(e, STATE_PROCESS_ERROR, SEND_PROOF_FEE_PAYMENT_CONFIRMATION);
    }
    return false;
  };

  const sendProofFeePayment = async (
    form: IFeePaymentProofForm,
  ): Promise<boolean> => {
    const token = getToken();
    const proof = prepareFeePaymentProof(form as IFeePaymentProofForm);
    try {
      let response: IStandardServiceResponse =
        await apiService.postFeePaymentProof(proof, token);
      if (response.success) {
        return true;
      }
    } catch (e) {
      handleError(e, STATE_PROCESS_ERROR, SEND_PROOF_FEE_PAYMENT);
    }
    return false;
  };

  const value = {
    ...state,
    fetchBalanceList,
    fetchHistoryList,
    fetchNotificationList,
    sendProof,
    sendEmailBalance,
    fetchBalanceSheet,
    fetchSemaphoreItems,
    fetchDebtorList,
    fetchReceivableListByDebtor,
    fetchSalesmanClients,
    setSalesmanClient,
    fetchDocumentList,
    fetchInvoiceFeeSummaryList,
    setInvoiceFeeSummary,
    setInvoiceFeeListToPay,
    fetchBasePaymentC2P,
    sendPaymentC2P,
    sendProofFeePayment,
    sendPaymentC2PConfirmation,
    sendPaymentCreditCard,
    actions: ACTION_VERBS,
  } as IContext;

  /**
   *
   */
  return <Context.Provider value={value}>{props.children}</Context.Provider>;
};
